import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, EASE } from "./Reveal";
import { scrollToSection } from "../lib/scroll";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });

    // Sky + peak: slow drift upward, gentle push-in
    const peakScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.22]);
    const peakY = useTransform(scrollYProgress, [0, 1], ["0%", "-16%"]);

    // Mid cloud bank: rises past the peak
    const farY = useTransform(scrollYProgress, [0, 1], ["58%", "-68%"]);
    const farScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.5]);

    // Foreground cloud bank: billows forward and swallows the frame
    const nearY = useTransform(scrollYProgress, [0, 0.82], ["100%", "-95%"]);
    const nearScale = useTransform(scrollYProgress, [0, 0.82], [1.1, 2.3]);
    const nearBlur = useTransform(
        scrollYProgress,
        [0, 0.5, 0.82],
        ["blur(0px)", "blur(2px)", "blur(13px)"],
    );

    // Wordmark
    const oContent = useTransform(scrollYProgress, [0, 0.26], [1, 0]);
    const sContent = useTransform(scrollYProgress, [0, 0.26], [1, 0.92]);
    const yContent = useTransform(scrollYProgress, [0, 0.26], [0, -90]);

    // Final dissolve into the ink background
    const oVeil = useTransform(scrollYProgress, [0.45, 0.84], [0, 1]);
    const oCue = useTransform(scrollYProgress, [0, 0.1], [1, 0]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative h-[320vh] bg-ink"
        >
            <div className="sticky top-0 h-screen overflow-hidden">
                {/* Sky + summit */}
                <motion.div
                    style={{ scale: peakScale, y: peakY }}
                    aria-hidden="true"
                    className="absolute inset-0 will-change-transform"
                >
                    <motion.img
                        src="/hero-peak.jpg"
                        alt=""
                        data-testid="hero-peak-image"
                        className="h-full w-full object-cover"
                        initial={{ scale: 1.1, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 2.4, ease: EASE }}
                    />
                </motion.div>

                {/* Cinematic grade over the photograph */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-ink/20"
                />
                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-b from-ink/65 via-ink/10 to-ink/60"
                />
                <div className="hero-glow" aria-hidden="true" />

                {/* Mid-depth cloud bank */}
                <motion.div
                    style={{ y: farY, scale: farScale }}
                    aria-hidden="true"
                    className="cloud-layer absolute inset-x-[-12%] bottom-0 h-[58vh] will-change-transform"
                >
                    <img
                        src="/hero-clouds-far.jpg"
                        alt=""
                        className="h-full w-full object-cover opacity-80"
                    />
                </motion.div>

                {/* Lower scrim — seats the typography on the cloud sea */}
                <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 z-10 h-[62%] bg-gradient-to-t from-ink via-ink/55 to-transparent"
                />

                {/* Wordmark lockup */}
                <motion.div
                    style={{ opacity: oContent, scale: sContent, y: yContent }}
                    className="absolute inset-x-0 top-[38%] z-20 flex -translate-y-1/2 flex-col items-center px-6 text-center"
                >
                    <motion.img
                        src="/mark-peak.png"
                        alt=""
                        aria-hidden="true"
                        data-testid="hero-mark"
                        className="w-[min(34vw,218px)] select-none drop-shadow-[0_6px_28px_rgba(0,0,0,0.55)]"
                        initial={{ opacity: 0, y: 26 }}
                        animate={{ opacity: 0.96, y: 0 }}
                        transition={{ duration: 1.8, ease: EASE }}
                    />
                    <h1
                        data-testid="hero-wordmark"
                        className="mt-5 font-label text-[clamp(1.5rem,5.2vw,3.5rem)] font-normal leading-none text-cream"
                        style={{ textShadow: "0 2px 26px rgba(0,0,0,0.65)" }}
                    >
                        <MaskedLine delay={0.4}>
                            <span className="tracking-[0.16em]">MOUNTAIN</span>
                            <span className="tracking-[0.16em] text-gold">
                                VIEW
                            </span>
                        </MaskedLine>
                    </h1>
                    <div
                        data-testid="hero-pictures-rule"
                        className="mt-4 flex w-full max-w-[min(74vw,470px)] items-center gap-5"
                    >
                        <motion.span
                            aria-hidden="true"
                            className="h-px flex-1 origin-right bg-gradient-to-l from-gold/80 to-transparent"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: 0.5, duration: 1.1, ease: EASE }}
                        />
                        <motion.span
                            className="font-label text-[clamp(0.62rem,1.6vw,0.95rem)] tracking-[0.62em] text-cream/90"
                            style={{ textShadow: "0 2px 18px rgba(0,0,0,0.7)" }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.55, duration: 0.9 }}
                        >
                            PICTURES
                        </motion.span>
                        <motion.span
                            aria-hidden="true"
                            className="h-px flex-1 origin-left bg-gradient-to-r from-gold/80 to-transparent"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: 0.5, duration: 1.1, ease: EASE }}
                        />
                    </div>
                    <p
                        data-testid="hero-tagline"
                        className="mt-8 font-display text-[clamp(1.05rem,2.3vw,1.7rem)] font-light leading-snug text-cream/90"
                        style={{ textShadow: "0 2px 22px rgba(0,0,0,0.7)" }}
                    >
                        <MaskedLine delay={0.62}>
                            <span>Three decades of cinema.</span>
                        </MaskedLine>
                        <MaskedLine delay={0.74}>
                            <span className="italic text-gold">
                                A new generation of stories.
                            </span>
                        </MaskedLine>
                    </p>
                </motion.div>

                {/* Foreground cloud bank — takes over the frame */}
                <motion.div
                    style={{ y: nearY, scale: nearScale, filter: nearBlur }}
                    aria-hidden="true"
                    className="cloud-layer absolute inset-x-[-18%] bottom-0 z-30 h-[72vh] will-change-transform"
                >
                    <img
                        src="/hero-clouds-near.jpg"
                        alt=""
                        className="h-full w-full object-cover"
                    />
                </motion.div>

                {/* Dissolve to ink */}
                <motion.div
                    style={{ opacity: oVeil }}
                    aria-hidden="true"
                    className="absolute inset-0 z-40 bg-ink"
                />

                <motion.div
                    style={{ opacity: oCue }}
                    className="absolute inset-x-0 bottom-8 z-40 flex justify-center"
                >
                    <motion.a
                        href="#legacy"
                        data-testid="hero-scroll-cue"
                        onClick={(e) => {
                            e.preventDefault();
                            scrollToSection("#legacy");
                        }}
                        className="flex flex-col items-center gap-3"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.1, duration: 0.9 }}
                    >
                        <span className="font-label text-[9px] tracking-[0.5em] text-cream/70">
                            SCROLL
                        </span>
                        <span className="scroll-line" aria-hidden="true" />
                    </motion.a>
                </motion.div>
            </div>
        </section>
    );
}
